// eslint-disable-next-line @altermaind/you-shall-not-pass
import { FC, useCallback, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { useForm } from 'react-hook-form';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Card, Loader, Typography } from '@altermaind/web/ui';
import { AlertBadge, ProgressBar, RenderLocalesWithComponents, Tabs } from '@/_shared/components';
import { Charts } from '@/_shared/components/Charts';
import { Form } from '@/_shared/components/Form';
import { Tooltip } from '@/_shared/components/Tooltip';
import { useNextRechargeInDays } from '@/_shared/hooks/useNextRechargeInDays';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';
import {
  postEmployerCreditsCall,
  postEmployerCreditsSummaryByCategoryCall,
} from '@/credits/state/api';
import { Employer } from '../../../state/types';
import { HistoryList } from './HistoryList';
import { LastThreeMonths } from './LastThreeMonths';

type Props = {
  employer: Employer;
};

export const CreditsUserDashboard: FC<Props> = ({ employer }) => {
  const t = useTranslations('people.details.credits');
  const tShared = useTranslations();
  const { genericResponsiveValue } = useResponsiveStyle();

  const { control, watch } = useForm();

  const fieldValues = watch();

  const { nextMonth, days } = useNextRechargeInDays();

  const filterItems = [
    { label: t('category.filters.monthly'), value: 'MONTHLY' },
    { label: t('category.filters.quarterly'), value: 'QUARTER' },
    { label: t('category.filters.semester'), value: 'SEMESTER' },
  ];

  const [tabs, setTabs] = useState([
    {
      title: t('tabs.income'),
      index: 0,
      active: false,
      dataTestId: 'tab1',
    },
    {
      title: t('tabs.spent'),
      index: 1,
      active: true,
      dataTestId: 'tab2',
    },
  ]);

  const setOpenTab = useCallback(
    (index: number) => {
      setTabs([...tabs.map(tab => ({ ...tab, active: tab.index === index }))]);
    },
    [tabs],
  );

  const {
    execute: executePostEmployerCredits,
    success: successPostEmployerCredits,
    loading: loadingPostEmployerCredits,
    error: errorPostEmployerCredits,
    totalAvailableCredits,
    monthlyCredits,
    consumedCredits,
    totalCredits,
    items,
    trend,
  } = postEmployerCreditsCall.hook();

  const {
    execute: executePostEmployerCreditsSummaryByCategory,
    response: responsePostEmployerCreditsSummaryByCategory,
  } = postEmployerCreditsSummaryByCategoryCall.hook();

  useEffect(() => {
    if (employer && employer.id) {
      executePostEmployerCredits({
        email: employer.email,
      });
    }
  }, [employer, executePostEmployerCredits]);

  useEffect(() => {
    if (employer && employer.id && fieldValues?.filterActivity) {
      executePostEmployerCreditsSummaryByCategory({
        email: employer.email,
        period: fieldValues?.filterActivity?.value || 'MONTHLY',
      });
    }
  }, [employer, executePostEmployerCreditsSummaryByCategory, fieldValues?.filterActivity]);

  if (loadingPostEmployerCredits) {
    return (
      <Box mt='s' textAlign='center'>
        <Loader size='s' />
      </Box>
    );
  }

  if (errorPostEmployerCredits) {
    const message = tShared('errors.genericApiError');
    return <AlertBadge containerProps={{ mt: 's' }} iconName='info' type='error' label={message} />;
  }

  if (successPostEmployerCredits && !loadingPostEmployerCredits && !errorPostEmployerCredits) {
    const progress = (consumedCredits / totalCredits) * 100;

    return (
      <Box>
        <Box mt='s'>
          <Card backgroundColor='primary'>
            <Box
              mb='s'
              display='flex'
              flexDirection={genericResponsiveValue({
                breakPoints: { desktop: 'row' },
                defaultValue: 'column',
              })}
              alignItems={genericResponsiveValue({
                breakPoints: { desktop: 'center' },
                defaultValue: 'flex-start',
              })}
              justifyContent='space-between'
            >
              <Typography variant='h1'>
                {employer.firstName} {employer.lastName}
              </Typography>
              <Typography variant='p2/light'>
                <RenderLocalesWithComponents
                  text={t('nextRecharge', {
                    date: '!{DATE}!',
                    days: days.toString(),
                  })}
                  placeholders={{
                    '!{DATE}!': (
                      <Typography as='span' variant='p2/light' fontWeight='500'>
                        {nextMonth}
                      </Typography>
                    ),
                  }}
                />
              </Typography>
            </Box>
            <Box display='flex' flexDirection='row' alignItems='center'>
              <Box flex='1'>
                <Typography as='span' variant='p2'>
                  {t('total.label')}
                </Typography>
                <Typography mt='xxxs' variant='special'>
                  {totalAvailableCredits}
                </Typography>
              </Box>
              <Box flex='1'>
                <Typography as='span' variant='p2'>
                  {t('total.currentMonth')}
                </Typography>
                <Typography mt='xxxs' variant='special'>
                  {monthlyCredits}
                </Typography>
              </Box>
            </Box>
            <Box>
              <Box mb='xs' mt='m' display='flex' flexDirection='row' alignItems='center'>
                <Typography mr='xxxs' variant='p2'>
                  {t('overall')}
                </Typography>
                <Tooltip>{t('overallTooltip')}</Tooltip>
              </Box>
              <ProgressBar currentPercentage={Number(progress.toFixed(0))} />
              <Box display='flex' justifyContent='flex-end' mt='xxs'>
                <Typography variant='p2/light'>
                  {consumedCredits} / {totalCredits}
                </Typography>
              </Box>
            </Box>
          </Card>
        </Box>
        <LastThreeMonths items={items} />
        <Typography mt='xl' variant='h3'>
          {t('trend')}
        </Typography>
        <Box
          display='grid'
          gridColumnGap='s'
          mt='xs'
          alignItems='stretch'
          gridTemplateColumns={genericResponsiveValue({
            breakPoints: { desktop: '1fr 1fr' },
            defaultValue: '1fr',
          })}
        >
          <Box display='flex' flexDirection='column' height='100%'>
            <Card
              display='flex'
              flexDirection='column'
              height='100%'
              justifyContent='space-between'
            >
              <Box>
                <Box display='flex' flexDirection='row' alignItems='center'>
                  <Typography mr='xxs' variant='h3'>
                    {t('averageMonthlyUsage')}
                  </Typography>
                  <Tooltip iconProps={{ size: 'xs' }} iconName='info'>
                    {t('averageMonthlyUsageTooltip')}
                  </Tooltip>
                </Box>
                <Typography variant='p2/light'>
                  {t('fromNow', { date: dayjs().format('DD/MM/YYYY') })}
                </Typography>
              </Box>
              <Typography mt='s' variant='special'>
                {trend.creditsConsumedMonthly}{' '}
                <Typography as='span' variant='p2/light'>
                  {t('creditLabel')}
                </Typography>
              </Typography>
            </Card>
            <Card
              justifyContent='space-between'
              display='flex'
              flexDirection='column'
              height='100%'
              mt='s'
            >
              <Box>
                <Box display='flex' flexDirection='row' alignItems='center'>
                  <Typography mr='xxs' variant='h3'>
                    {t('trendLastMonths')}
                  </Typography>
                  <Tooltip iconProps={{ size: 'xs' }} iconName='info'>
                    {t('trendLastMonthsTooltip')}
                  </Tooltip>
                </Box>
                <Typography variant='p2/light'>
                  {`${tShared(`months.${dayjs().format('MMMM').toLowerCase()}`)} ${dayjs().format('YYYY')}`}
                </Typography>
              </Box>
              <Box
                display='flex'
                alignItems='center'
                justifyContent='space-between'
                flexDirection='row'
                mt='s'
              >
                <Typography variant='special'>
                  {`${trend.progress}%`}{' '}
                  {trend.progress > 0 && (
                    <Typography
                      as='span'
                      color={trend.progress >= 0 ? 'success' : 'error'}
                      variant='p2/light'
                    >
                      {trend.progress >= 0 ? '\u2191' : '\u2193'}
                    </Typography>
                  )}
                </Typography>
                {trend.creditsConsumed > 0 && (
                  <Box px='xxs' display='inline-block' borderRadius='xxl' backgroundColor='primary'>
                    <Typography variant='p2' fontSize='xs'>
                      {trend.creditsConsumed} {t('creditLabel')}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Card>
          </Box>
          <Card
            mt={genericResponsiveValue({
              breakPoints: { desktop: 'none' },
              defaultValue: 's',
            })}
          >
            <Box mb='xs' display='flex' flexDirection='row' alignItems='center'>
              <Typography mr='xxs' variant='h3'>
                {t('incomeAndSpent.title')}
              </Typography>
              <Tooltip iconProps={{ size: 'xs' }} iconName='info'>
                {t('incomeAndSpent.tooltip')}
              </Tooltip>
            </Box>
            <Box
              display='flex'
              mt='xs'
              flexDirection={genericResponsiveValue({
                breakPoints: { desktop: 'row' },
                defaultValue: 'column',
              })}
              justifyContent='flex-end'
            >
              <Box
                mr={genericResponsiveValue({
                  breakPoints: { desktop: 'xxs' },
                  defaultValue: 'none',
                })}
                mb={genericResponsiveValue({
                  breakPoints: { desktop: 'none' },
                  defaultValue: 'xs',
                })}
              >
                <Form.FormDropdown
                  control={control}
                  name='filterActivity'
                  items={filterItems || []}
                  additonalProps={{
                    required: true,
                  }}
                  defaultValue={{
                    value: 'MONTHLY',
                    label: t('category.filters.monthly'),
                  }}
                  data-testid='dropdown-select-filter-activity'
                  optionTestId='dropdown-select-filter-activity-option'
                  backgroundColorHeader='grey/dark/background'
                />
              </Box>
              <Tabs
                backgroundColor='grey/dark/background'
                buttonVariant='primary'
                setActiveTab={setOpenTab}
                tabs={tabs}
              />
            </Box>
            {tabs.map(tab => {
              return (
                <Box key={tab.title} display='flex' flexDirection='column'>
                  {tab.index === 0 && tab.active && (
                    <Box>
                      {responsePostEmployerCreditsSummaryByCategory && (
                        <>
                          {responsePostEmployerCreditsSummaryByCategory.income.length === 0 && (
                            <Box
                              display='flex'
                              alignItems='center'
                              justifyContent='center'
                              height='100%'
                              minHeight='120px'
                              width='100%'
                              mt='l'
                            >
                              <Typography mt='s' variant='p2/light'>
                                {t('incomeAndSpent.noData')}
                              </Typography>
                            </Box>
                          )}
                          {responsePostEmployerCreditsSummaryByCategory.income.length > 0 && (
                            <Charts.PieChartComponent
                              data={responsePostEmployerCreditsSummaryByCategory.income.map(
                                item => ({
                                  name: tShared(`credits.${item.eventType}`) || 'N/A',
                                  value: item.credits,
                                }),
                              )}
                            />
                          )}
                        </>
                      )}
                    </Box>
                  )}
                  {tab.index === 1 && tab.active && (
                    <Box>
                      {responsePostEmployerCreditsSummaryByCategory && (
                        <>
                          {responsePostEmployerCreditsSummaryByCategory.spent.length === 0 && (
                            <Box
                              display='flex'
                              alignItems='center'
                              justifyContent='center'
                              height='100%'
                              minHeight='120px'
                              width='100%'
                              mt='l'
                            >
                              <Typography mt='s' variant='p2/light'>
                                {t('incomeAndSpent.noData')}
                              </Typography>
                            </Box>
                          )}
                          {responsePostEmployerCreditsSummaryByCategory.spent.length > 0 && (
                            <Charts.PieChartComponent
                              data={responsePostEmployerCreditsSummaryByCategory.spent.map(
                                item => ({
                                  name: tShared(`credits.${item.eventType}`) || 'N/A',
                                  value: item.credits,
                                }),
                              )}
                            />
                          )}
                        </>
                      )}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Card>
        </Box>
        <HistoryList emailEmployer={employer.email} />
      </Box>
    );
  }

  return null;
};
